import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useUsersContext } from "../../context/usersContext";
import { useUserInfo } from "../../context/user";
import PromptModal from "./components/PromptModal";
import ProvenTaskGrid from "./components/ProvenTaskGrid";
import Uploadfile from "../Chat/components/uploadFiles/index";

const getUtcOffset = () => {
  const offsetInMinutes = -new Date().getTimezoneOffset();
  const sign = offsetInMinutes >= 0 ? "+" : "-";
  const absoluteMinutes = Math.abs(offsetInMinutes);
  const hours = String(Math.floor(absoluteMinutes / 60)).padStart(2, "0");
  const minutes = String(absoluteMinutes % 60).padStart(2, "0");
  return `${sign}${hours}:${minutes}`;
};

const PromptPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { tasksData, fetchTasksData, selectedAssistant } = useUsersContext();
  const [, , , , userActiveLocation] = useUserInfo();

  const timeZone = moment.tz.guess() || "UTC";
  const isEdit = Boolean(id) && id !== "new";

  const editadata = useMemo(() => {
    if (!isEdit) return null;
    const list = tasksData?.prompt || [];
    return list.find((task) => (task?._id || task?.id) === id) || null;
  }, [isEdit, id, tasksData]);

  useEffect(() => {
    if (!tasksData?.prompt) {
      fetchTasksData?.();
    }
  }, [tasksData, fetchTasksData]);

  const fallbackAssistant = selectedAssistant || {
    assistant_name: "Brand AI",
    modal_name: "openai/gpt-4o",
    assistant_id: userActiveLocation?.assistant_id,
    status: true,
  };

  const handleClose = () => {
    // Return to the chat home with its Task Library expanded (the standalone
    // All-Tasks page was merged into the landing under this anchor).
    navigate("/app/chat#task-library");
  };

  // A task saved without a date part (e.g. a daily schedule) stores
  // scheduleTime as " 09:00", which moment cannot parse — formatting that blind
  // is what printed the literal "Invalid date" in the schedule summary. Fall
  // back to the HH:mm found in the raw string.
  const savedMoment = moment.tz(editadata?.scheduleTime, timeZone);
  const timeWithZone = savedMoment.isValid()
    ? savedMoment.format("HH:mm")
    : (editadata?.scheduleTime || "").trim().match(/\d{1,2}:\d{2}/)?.[0] || "";

  const autoloadfirsttext = location.state?.autoloadfirsttext || "";
  const resumeTaskId = location.state?.resumeTaskId || "";
  const resumeTaskTitle = location.state?.resumeTaskTitle || "";
  const resumeTaskContent = location.state?.resumeTaskContent || "";
  const [attachedFiles, setAttachedFiles] = useState([]);

  // Same attach control as the New Task composer (ChatInput) — Uploadfile with
  // no chatId is the landing-composer mode; uploads land on the account.
  const composerAttachSlot = !isEdit ? (
    <div className="flex items-center gap-2">
      <Uploadfile
        classname="!outline-none shadow-none"
        chatId={undefined}
        onFileSelect={(files) =>
          setAttachedFiles((prev) => [...prev, ...(files || [])])
        }
      />
      {attachedFiles.length > 0 && (
        <span className="text-[12px] text-muted-foreground">
          {attachedFiles.length} attached files
        </span>
      )}
    </div>
  ) : null;

  // Lovable /create-task framing (CU-86carc99u): eyebrow + display + lede
  // above the composer, proven-task grid below. Create flow only — the edit
  // flow keeps the bare wizard.
  const pageHeader = !isEdit ? (
    <div className="page-header [&_.page-display]:text-3xl [&_.page-display]:md:text-4xl [&_.page-lede]:text-sm pb-2">
      <span className="page-eyebrow">Automate</span>
      <h1 className="page-display">
        What should we <span className="accent">automate?</span>
      </h1>
      <p className="page-lede">
        Describe it once. Describe a Task and outline a process; you can attach
        meeting notes and call transcripts or pick a proven Task below.
      </p>
    </div>
  ) : null;

  return (
    <PromptModal
      pageHeader={pageHeader}
      pageFooter={!isEdit ? <ProvenTaskGrid /> : null}
      composerAttachSlot={composerAttachSlot}
      renderAs="page"
      onClose={handleClose}
      justCloseIt={handleClose}
      getUpdatePromptsData={fetchTasksData}
      editadata={editadata}
      autoloadfirsttext={autoloadfirsttext}
      resumeTaskId={resumeTaskId}
      resumeTaskTitle={resumeTaskTitle}
      resumeTaskContent={resumeTaskContent}
      time_zone={timeZone}
      timeWithZone={timeWithZone}
      utcOffset={getUtcOffset()}
      assistantData={
        fallbackAssistant?.assistant_id ? [fallbackAssistant] : []
      }
      asistantSelected={fallbackAssistant}
    />
  );
};

export default PromptPage;
